/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { PlanState } from 'src/app/services/plan-state.service';
import { TicketList } from '../common/fancy-firebase-ticket/lists/TicketList';
import { Ticket } from '@touchplan/common';
import { genPrettyDependencyList } from 'src/app/app-data/models/Dependencies';
import * as strings from '@touchplan/strings';
import { PlanUndosService } from '../modules/plan/plan-undos.service';
import { serializeTicket, metaizeTicket, getTimelineIdForTicket } from 'src/app/common/undo/undo-utils';
import { TicketMeta } from '../app-data/models/TicketMeta';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { DependencyDeletionComponent } from '../components/popups';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from '../common/generic-dialog/generic-dialog.component';

interface ITryDeleteTicketsConfig {
	disableConfirmation?: boolean;
	ticketListOverride?: TicketList;
}

@Injectable()
export class ValidatedPlanActions {
	private logger: ILoggerService;

	constructor(private planState: PlanState, private planUndos: PlanUndosService, private dialog: MatDialog) {
		this.logger = LoggerServiceFactory.create('ValidatedPlanActions');
	}

	//used by undo, consider moving elsewhere
	// returns the combinedId if the ticket is in the timeline, null, if it's not
	private getTimelineIdForTicket(ticket: Ticket) {
		return getTimelineIdForTicket(ticket, this.planState.timelineTickets, this.planState.planId);
	}

	public tryDeleteTickets(
		tickets: Map<string, Ticket>,
		config: ITryDeleteTicketsConfig = { disableConfirmation: false }
	) {
		const ticketList = config.ticketListOverride || this.planState.tickets;
		const links = this.planState.dependencies.getLinks(tickets);
		const filteredLinks = this.planState.dependencies.filterLinksToOnlyExternal(links, tickets);

		const planDataGetter = (planId: string) => {
			const plan = this.planState.plansApi.data.syncList.get(planId);
			return plan ? plan.view : null;
		};

		// If there are dependencies across plans, get confirmation from the user about the deletion:
		let deleteTicket$: Observable<boolean>;
		const uiDeps = genPrettyDependencyList(filteredLinks, this.planState.planId, planDataGetter);
		if (uiDeps.length && ticketList !== this.planState.floatingTickets) {
			const onlyCancel = false;
			deleteTicket$ = DependencyDeletionComponent.showDialog(uiDeps, onlyCancel, this.dialog);
		}
		// Get general confirmation from the user about the deletion:
		else if (!config.disableConfirmation) {
			const confDialogRef = GenericDialogComponent.displayConfirmationDialog(
				this.dialog,
				strings.MULTI_TICKET_DELETE_TITLE,
				strings.MULTI_TICKET_DELETE_BUTTON_TEXT,
				strings.DEFAULT_POPUP_CANCEL_BUTTON
			);
			deleteTicket$ = confDialogRef.afterClosed();
		} else {
			deleteTicket$ = of(true);
		}

		deleteTicket$
			.pipe(
				take(1),
				filter((shouldDeleteTickets) => !!shouldDeleteTickets)
			)
			.subscribe((output) => {
				const predecessorEditedTickets: TicketMeta[] = [];

				//remove dependencies
				if (uiDeps) {
					//get only the links where the predecessor must be a selected ticket
					const filteredDeps = this.planState.dependencies.filterOutLinks(links, tickets, true, false);
					if (!filteredDeps.isEmpty) {
						const outputLinks = this.planState.dependencies.massUpdateLinks(filteredLinks, false);
						const ticketsToPersist = new Map();
						outputLinks.outputLinks.forEachy((srcId, targetId, outputTickets) => {
							if (ticketsToPersist.has(srcId)) {
								return;
							} //already did this one
							//push into list for undo
							predecessorEditedTickets.push(
								metaizeTicket(
									serializeTicket(outputTickets[0]),
									this.getTimelineIdForTicket(outputTickets[0])
								)
							);
							//patch in successor linked tickets for undo
							predecessorEditedTickets.push(
								metaizeTicket(
									serializeTicket(outputTickets[1]),
									this.getTimelineIdForTicket(outputTickets[1])
								)
							);

							//skip removing dependencies from tickets that will be deleted
							if (tickets.has(outputTickets[0].$id)) {
								return;
							}
							this.planState.actions.updateTicketLiveData(
								srcId,
								{
									predecessors: outputLinks.writeData[srcId]['predecessors'],
								},
								outputTickets[0].isTimelineTicket ? this.planState.timelineTickets : this.planState.tickets
							);
							ticketsToPersist.set(srcId, outputTickets[0]);
						});
						//filter out tickets that are already being deleted
						// const predUpdateButNotDeleted = [];
						// ticketsToPersist.forEach(t => { if (!tickets.has(t.$id)) { predUpdateButNotDeleted.push(t); } });
						this.planState.actions.persistTicketData(ticketsToPersist);
					}
				}

				//manually trigger deletion undo
				if (ticketList === this.planState.tickets) {
					//before
					const startTime = new Date().getTime();
					const metadDeletedTicketsObsArray = Array.from(tickets.values()).map((t) => {
						//TODO - this one likely wants to be serialized long term. But that depends on more of a proper solution
						const customFields = this.planState.customFieldsApi.cloneCustomFieldTicketValues(t.$id);
						const attachment$ = this.planState.fileAttachCloneService.getTicketAttachment$(
							this.planState.projectId,
							this.planState.planId,
							t.$id
						);
						const ticketMeta$ = attachment$.pipe(
							take(1),
							map((ticketAttachmentList) =>
								metaizeTicket(t, this.getTimelineIdForTicket(t), customFields, ticketAttachmentList[t.$id])
							)
						);
						return ticketMeta$;
					});

					// The time required to execute this "forkJoin" and "subscribe" needs to remain small at scale.
					// We don't want the removal of the tickets from the plan to take an appreciable amount of time,
					// i.e. enough time for the user to do something with the tickets between deleting them and the
					// tickets actually disappearing.
					forkJoin(metadDeletedTicketsObsArray)
						.pipe(take(1))
						.subscribe((ticketMetaArray) => {
							// filter out deleted tickets from the dependency list
							const deletedTicketsCache: { [key: string]: boolean } = {};
							ticketMetaArray.forEach((t) => (deletedTicketsCache[t.ticket.$id] = true));
							const metadDependencyTickets = predecessorEditedTickets.filter(
								(tm) => !deletedTicketsCache[tm.ticket.$id]
							);

							// after
							const postUndoMetadDependencyTickets = metadDependencyTickets.map((oldMeta) => {
								const metaTicketList = oldMeta.ticket.isTimelineTicket
									? this.planState.timelineTickets
									: this.planState.tickets;
								const currentTicket = metaTicketList.syncList.get(oldMeta.ticket.$id);
								if (!currentTicket) {
									throw new Error('Dependant ticket deleted mid remove');
								} //...not really sure what to do here
								return metaizeTicket(
									serializeTicket(currentTicket),
									this.getTimelineIdForTicket(currentTicket)
								);
							});

							this.planUndos.ticketDeleteOperation.snapshotUndoState([
								{ deletedTickets: ticketMetaArray, dependencyTickets: metadDependencyTickets },
								{ deletedTickets: null, dependencyTickets: postUndoMetadDependencyTickets },
							]);
							// remove tickets
							const endTime = new Date().getTime();
							this.logger.log('time delta (ms) = ' + (endTime - startTime));
							this.planState.actions.deleteTicket(tickets, ticketList);
						});
				} else {
					// remove tickets
					this.planState.actions.deleteTicket(tickets, ticketList);
				}
			});
	}
}
